import { GeoPoint } from '@@partner:types:app'
import { curryN, keys, reduce } from 'rambda'

/*
  Fill number with leading zeros
*/

export const zfill = (number: number): string =>
  number < 10 ? `0${number}` : `${number}`

/*
  port of mapObjIndexed from ramda
*/

export const mapObjIndexed = curryN(2, (fn, obj: Record<string, any>) => {
  return reduce(
    (acc, key) => {
      acc[key] = fn(obj[key], key, obj)
      return acc
    },
    {} as any,
    keys(obj)
  )
})

/*
  Parse json or return default value
*/

export const parseJsonSilent = (
  data: string | undefined,
  defaultValue: any
) => {
  if (!data) {
    return defaultValue
  }

  try {
    return JSON.parse(data)
  }
  catch (_parseError) {
    return defaultValue
  }
}

/*
  Convert phone number to numbers-only format
*/

export const normalizePhoneNumber = (phone = '') => {
  if (phone === null) return ''
  return phone.replace(/\D*/g, '')
}

/*
  Convert all null values to empty strings
*/

export function convertNullsToEmptyStrings<T extends Record<string, any>> (obj: T) {
  return Object.entries(obj).reduce(
    (prev, [ key, val ]) => ({
      ...prev,
      [key]: val !== null ? val : ''
    }),
    {} as T
  )
}

/*
  Format date object to string with format DD.MM.YYYY HH:MM
*/

export const formatDate = (date: Date, showTime = true) => {
  const formattedDate = `${zfill(date.getDate())}.${zfill(
    date.getMonth() + 1
  )}.${zfill(date.getFullYear())}`

  if (!showTime) {
    return formattedDate
  }

  const formattedTime = `${zfill(date.getHours())}:${zfill(date.getMinutes())}`

  return `${formattedDate} ${formattedTime}`
}

export const formatShortDate = (date: Date) => {
  const formattedTime = `${zfill(date.getHours())}:${zfill(date.getMinutes())}`

  if (new Date().setHours(0,0,0,0) === date.setHours(0,0,0,0)) {
    return formattedTime
  }

  const formattedDate = `${zfill(date.getDate())}.${zfill(
    date.getMonth() + 1
  )}.${zfill(date.getFullYear())}`

  return `${formattedDate} ${formattedTime}`
}

export const formatDayMonthYear = (date: Date) => {
  return `${zfill(date.getDate())}.${zfill(date.getMonth() + 1)}.${zfill(
    date.getFullYear()
  )}`
}

export const formatSqlDateWithTZ = (date: Date, timezone: number) => {
  return `${zfill(date.getFullYear())}-${zfill(date.getMonth() + 1)}-${zfill(
    date.getDate()
  )} ${zfill(date.getHours())}:${zfill(date.getMinutes())}:00+${zfill(
    timezone
  )}`
}

export const moveDateToTimezone = (date: number, timezone: number): number => {
  const currentTimeZone = new Date().getTimezoneOffset()
  const diffInMs = (currentTimeZone + timezone / 60) * 60 * 1000
  const finalDate = date + diffInMs

  return finalDate
}

/*
  Returns currency symbol
*/

export const getCurrencySign = () => import.meta.env.VITE_APP_CURRENCY_SYMBOL

/*
  Format price to currency like format and add currency symbol
*/

export const formatPrice = (value: number | string = 0) => {
  const price = parseFloat(value?.toString()) || 0
  return `${price.toFixed(2)} ${getCurrencySign()}`
}

/*
  Format value as percent
*/

export const formatPercent = (value: number = 0) => {
  return `${value.toFixed(2)}%`
}

/*
  Format xid value, drop leading zeros
*/

export const formatXid = (xid: string = '') => {
  if (xid && /\d+/.test(xid)) {
    return xid.replace(/^0*(.*)$/g, '$1')
  }

  return ''
}

export const formatWeight = (value = 0) => {
  const gramms = value || 0
  if (gramms < 1000) {
    return `${gramms} гр.`
  }
  return `${(gramms / 1000).toFixed(2)} кг.`
}

/*
  Format phone number to +7 (999) 999-99-99 or 8 (9999) 99-99-99
*/

export const formatPhone = (phone: string) => {
  const normalized = normalizePhoneNumber(phone)
  const [ lead, ...digits ] = normalized.split('')

  if (digits.length < 10) {
    return phone
  }

  if (parseInt(lead) === 7) {
    return `+${lead} (${digits.slice(0, 3).join('')}) ${digits
      .slice(3, 6)
      .join('')}-${digits.slice(6, 8).join('')}-${digits.slice(8).join('')}`
  }
  else {
    return `${lead} (${digits.slice(0, 4).join('')}) ${digits.slice(
      4,
      6
    )}-${digits.slice(6, 8).join('')}-${digits.slice(8).join('')}`
  }
}

/*
  Checks if geopoint is valid
*/

export const isGeoPointValid = (geoPoint: any) => {
  const { longitude = NaN, latitude = NaN } = geoPoint || {}
  return !isNaN(parseFloat(longitude)) && !isNaN(parseFloat(latitude))
}

/*
  Make sure that geopoint has valid format
*/

export const normalizeGeoPoint = (geoPoint: any) => {
  const { longitude = NaN, latitude = NaN } = geoPoint || {}
  const normalizedLongitude = 
    parseFloat(longitude) > 180 ? 180 
      : parseFloat(longitude) < -180 ? -180 
        : parseFloat(longitude)
  const normalizedLatitude = parseFloat(latitude) > 180 ? 180 
    : parseFloat(latitude) < -180 ? -180 
      : parseFloat(latitude)
  return {
    longitude: normalizedLongitude,
    latitude: normalizedLatitude
  }
}

export const normalizeGeoPolygon = (geoPoints: GeoPoint[]) => {
  return geoPoints.map(geoPoint => normalizeGeoPoint(geoPoint))
}

export const ensureUrlProtocol = (value: any) => {
  if (value && !/^(https?)+:\/\//.test(value)) {
    return (value = `https://${value}/`)
  }

  return value
}

/*
  adding declensions to the date, where the data are
  numbers, and the endings of words are obtained from 
  an array of words
*/

export const makeWordsEndings = (data: number, words: string[]) => {
  let cases = [ 2, 0, 1, 1, 1, 2 ]

  return (
    data.toString() +
    words[
      data % 100 > 4 && data % 100 < 20
        ? 2
        : cases[data % 10 < 5 ? data % 10 : 5]
    ]
  )
}

export const convertToInt = (number: string | number) => {
  if (typeof number === 'number') {
    return number
  }
  else {
    return parseInt(number)
  }
}

export type Paths<T> = T extends Array<infer U>
  ? `${Paths<U>}`
  : T extends object
  ? {
      [K in keyof T & (string | number)]: K extends string
        ? `${K}` | `${K}.${Paths<T[K]>}`
        : never;
    }[keyof T & (string | number)]
  : never;

export const commonFieldChange = <T extends Record<string, any>>(
  setState: React.Dispatch<React.SetStateAction<T>>,
  hasSubObjects?: boolean
) => {
  const handleFieldChange =
    <K extends Paths<T>>(field: K) =>
      (value: T[K]) => {
        if (hasSubObjects && typeof field == 'string' && field.indexOf('.') !== -1) {
          const fields = field.split('.')
          setState((state) => ({
            ...state,
            [fields[0]]: {
              ...state[fields[0]],
              [fields[1]]: value
            }
          }))
        }
        else {
          setState((state) => ({
            ...state,
            [field]: value
          }))
        }
      }

  return handleFieldChange
}
